import { Component, Element, State, h, Host, Prop, Watch } from "@stencil/core";
import {
    getLocalStorageTokens,
    persistResultsInLocalStorage,
    verifyToken,
} from "./gated-content-utils";
import { FormResponse } from "./gated-content-types";

/** static base className */
const baseClass = "ux-iws-gated-content";
type GatedContentStatus =
    | "initial"
    | "loading" // verifying token
    | "token-valid" // token was confirmed by server
    | "blocked" // user is blocked
    | "form" // show form
    | "submitting" // submitting form
    | "submitted" // form submitted successfully
    | "error";

/**
 * This component is used to hide content behind a gate. The gate is a form that the user must fill out to access the content.
 * The form will send an email to the user with a link to the content.
 * The link includes a JWT token as query parameter "gc-token" that is used to verify the user.
 * That token should be verified by the server before the content is displayed. The token should be stored in local storage.
 */

@Component({
    tag: "ux-iws-gated-content",
    styleUrl: "gated-content.scss",
    shadow: true,
})
export class GatedContent {
    private formElement: HTMLFormElement | null = null;
    private uxForm: HTMLUxFormElement | null = null;
    private slotContainer: HTMLDivElement | null = null;

    @Element() host: HTMLUxIwsGatedContentElement;

    /**
     * The url to send the token to for verification.
     */
    @Prop() verifyTokenUrl: string = "";

    /**
     * Button text to show the form.
     */
    @Prop() buttonText: string = "Register for free";

    @State() status: GatedContentStatus = "initial";

    componentDidLoad() {
        this.handleTokens();
        window.setTimeout(() => {
            this.initForm();
        }, 1000);
    }

    private initForm() {
        const formSlot = this.host.querySelector("[slot='form']");
        this.formElement = formSlot?.querySelector("form") || null;
        if (this.formElement === null) {
            this.status = "error";
            return;
        }
        this.uxForm = formSlot?.querySelector("ux-form") || null;
        if (this.uxForm === null) {
            this.status = "error";
            return;
        }
        this.uxForm.addEventListener("uxFormValidated", (event) => {
            if (event.detail.isValid) {
                event.preventDefault();
                this.submitForm();
            }
        });
    }

    @Watch("verifyTokenUrl")
    private handleTokens() {
        // get token from url or from local storage
        const tokenParam = new URLSearchParams(window.location.search).get(
            "gc-token"
        );
        if (tokenParam !== null) {
            const url = new URL(window.location.href);
            url.searchParams.delete("gc-token");
            window.history.replaceState(null, "", url.toString());
        }
        const localStorageTokens: string[] = getLocalStorageTokens();
        const tokens =
            tokenParam !== null && tokenParam !== ""
                ? [tokenParam]
                : localStorageTokens;
        if (tokens.length > 0) {
            this.status = "loading";
            // send tokens to server for verification
            Promise.all(
                tokens.map((token: string) =>
                    verifyToken(token, this.verifyTokenUrl)
                )
            )
                .then((results) => {
                    if (results.map(({ result }) => result).includes("valid")) {
                        this.status = "token-valid"; // token was confirmed by server
                    } else if (
                        results
                            .map(({ result }) => result)
                            .includes("blacklisted")
                    ) {
                        this.status = "blocked"; // user is blocked
                    } else if (
                        results.map(({ result }) => result).includes("invalid")
                    ) {
                        this.status = "initial"; // user has a token but it is invalid for this page
                    } else {
                        this.status = "error";
                    }
                    return results;
                })
                .then(persistResultsInLocalStorage);
        }
    }

    private submitForm() {
        this.status = "submitting";
        const config = this.formElement.action.includes("/mock-data/")
            ? { method: "GET" }
            : {
                  method: "POST",
                  body: new FormData(this.formElement),
              };
        fetch(this.formElement.action, config)
            .then((response) => response.json())
            .then((response: FormResponse) => {
                if ("error" in response) {
                    this.status = "error";
                    return;
                }
                switch (response.do) {
                    case "setToken":
                        this.status = "token-valid";
                        persistResultsInLocalStorage([
                            {
                                token: response.arg,
                                result: "valid",
                            },
                        ]);
                        break;
                    case "showSlot":
                        switch (response.arg) {
                            case "thanks":
                                this.status = "submitted";
                                break;
                            case "blocked":
                                this.status = "blocked";
                                break;
                            case "error":
                                this.status = "error";
                                break;
                        }
                        break;
                    case "showMarkup":
                        this.status = "form";
                        this.host.querySelector("[slot='form']").innerHTML =
                            response.arg;
                        this.initForm();
                        break;
                    default:
                        this.status = "error";
                }
            })
            .catch(() => {
                this.status = "error";
            });
    }

    @Watch("status")
    private() {
        const whenToScroll: GatedContentStatus[] = [
            "error",
            "form",
            "submitting",
            "submitted",
        ];
        if (whenToScroll.includes(this.status)) {
            this.slotContainer?.scrollIntoView({ behavior: "smooth" });
        }
    }

    render() {
        return (
            <Host data-testid="gated-content">
                <div class={`${baseClass}`}>
                    <div
                        class={`${baseClass}__content ${
                            this.status === "token-valid"
                                ? `${baseClass}__content--visible`
                                : ""
                        }`}
                    >
                        <slot name="content"></slot>
                    </div>
                    <div ref={(el) => (this.slotContainer = el)}>
                        {this.status !== "token-valid" && (
                            <div class={`${baseClass}__overlay`}>
                                {(this.status === "initial" ||
                                    this.status === "form") && (
                                    <slot name="overlay-content"></slot>
                                )}
                                {this.status === "initial" && (
                                    <div class={`${baseClass}__button`}>
                                        <ux-button
                                            variant="primary"
                                            onClick={() =>
                                                (this.status = "form")
                                            }
                                            data-testid="show-form"
                                        >
                                            {this.buttonText}
                                        </ux-button>
                                    </div>
                                )}
                                {this.status === "form" && (
                                    <slot name="form"></slot>
                                )}
                                {this.status === "loading" && (
                                    <slot name="loading"></slot>
                                )}
                                {this.status === "submitting" && (
                                    <slot name="submitting"></slot>
                                )}
                                {this.status === "submitted" && (
                                    <slot name="thanks"></slot>
                                )}
                                {this.status === "blocked" && (
                                    <slot name="blocked"></slot>
                                )}
                                {this.status === "error" && (
                                    <slot name="error"></slot>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </Host>
        );
    }
}
